import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Building2 } from "lucide-react";
import { api } from "@/lib/api";
import { TEHSILS } from "@/mock/data";

const TEH_LABEL = Object.fromEntries(TEHSILS.filter((t) => t.value).map((t) => [t.value, t.label]));

function SdoCell({ r, d }) {
  return <><span className="font-semibold text-sky-700">{r || 0}</span><span className="text-slate-400"> / </span><span className="font-semibold text-emerald-700">{d || 0}</span></>;
}

function TotalRow({ rows, totalDrafts }) {
  const s = rows.reduce((a, r) => ({
    total: a.total + (r.total || 0), pe: a.pe + (r.prelimEligible || 0), pi: a.pi + (r.prelimIneligible || 0),
    pp: a.pp + (r.prelimPending || 0), vd: a.vd + (r.visitDone || 0), vp: a.vp + (r.visitPending || 0),
    draft: a.draft + (r.draft || 0), sr: a.sr + (r.sdoReceived || 0), sd: a.sd + (r.sdoDisposed || 0),
  }), { total: 0, pe: 0, pi: 0, pp: 0, vd: 0, vp: 0, draft: 0, sr: 0, sd: 0 });
  const draftTotal = totalDrafts === undefined ? s.draft : totalDrafts;
  return (
    <tr className="border-t-2 border-[#1b5e3f] bg-[#f3efe2] font-bold" data-testid="dashboard-total-row">
      <td className="p-2.5"></td>
      <td className="p-2.5 text-[#1b5e3f]">एकूण</td>
      <td className="p-2.5 text-center text-slate-500" data-testid="dashboard-total-drafts">{draftTotal}</td>
      <td className="p-2.5 text-center">{s.total}</td>
      <td className="p-2.5 text-center"><span className="text-emerald-700">{s.pe}</span><span className="text-slate-400"> / </span><span className="text-red-600">{s.pi}</span></td>
      <td className="p-2.5 text-center text-amber-700">{s.pp}</td>
      <td className="p-2.5 text-center" data-testid="dashboard-total-sdo"><SdoCell r={s.sr} d={s.sd} /></td>
      <td className="p-2.5 text-center">{s.vd}</td>
      <td className="p-2.5 text-center text-amber-700">{s.vp}</td>
    </tr>
  );
}

function StatsTable({ rows }) {
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-[#1b5e3f] text-left text-white">
              <th className="p-2.5 font-semibold">अ.क्र.</th>
              <th className="p-2.5 font-semibold">स्थानिक स्वराज्य संस्थेचे नाव</th>
              <th className="p-2.5 text-center font-semibold">प्रारूप अर्ज</th>
              <th className="p-2.5 text-center font-semibold">एकूण प्राप्त अर्ज</th>
              <th className="p-2.5 text-center font-semibold">प्राथमिक पडताळणी झालेले (पात्र/अपात्र)</th>
              <th className="p-2.5 text-center font-semibold">प्राथमिक पडताळणीसाठी प्रलंबीत</th>
              <th className="p-2.5 text-center font-semibold">SDO कडील अर्ज (प्राप्त/निकाली)</th>
              <th className="p-2.5 text-center font-semibold">प्रत्यक्ष भेटीद्वारे पडताळणी झालेले</th>
              <th className="p-2.5 text-center font-semibold">प्रत्यक्ष भेट पडताळणीसाठी प्रलंबीत</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-slate-400">कोणतेही अर्ज नाहीत</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.localBody} className="border-b border-slate-100">
                <td className="p-2.5">{i + 1}</td>
                <td className="p-2.5 font-semibold text-[#1b3d2a]">{r.localBody}</td>
                <td className="p-2.5 text-center font-semibold text-slate-500">{r.draft || 0}</td>
                <td className="p-2.5 text-center font-bold">{r.total}</td>
                <td className="p-2.5 text-center"><span className="font-semibold text-emerald-700">{r.prelimEligible}</span><span className="text-slate-400"> / </span><span className="font-semibold text-red-600">{r.prelimIneligible}</span></td>
                <td className="p-2.5 text-center font-semibold text-amber-700">{r.prelimPending}</td>
                <td className="p-2.5 text-center"><SdoCell r={r.sdoReceived} d={r.sdoDisposed} /></td>
                <td className="p-2.5 text-center">{r.visitDone}</td>
                <td className="p-2.5 text-center font-semibold text-amber-700">{r.visitPending}</td>
              </tr>
            ))}
            {rows.length > 0 && <TotalRow rows={rows} />}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[12.5px] text-slate-400">पात्र = हिरवा, अपात्र = लाल. या तहसीलमधील सर्व अर्जांची स्थिती संस्थानिहाय दाखवली आहे.</p>
    </>
  );
}

function TehsilRows() {
  const [rows, setRows] = useState([]);
  const [totalDrafts, setTotalDrafts] = useState(undefined);
  const [openT, setOpenT] = useState(null);
  const [detail, setDetail] = useState({});

  useEffect(() => {
    api.officerTehsilStats().then((d) => {
      const list = Array.isArray(d) ? d : (d?.rows || []);
      setRows(list);
      if (d && !Array.isArray(d) && d.totalDrafts !== undefined) setTotalDrafts(d.totalDrafts);
    }).catch(() => {});
  }, []);

  const toggle = async (t) => {
    const next = openT === t ? null : t;
    setOpenT(next);
    if (next && detail[t] === undefined) {
      try { const d = await api.officerLocalBodyStats(t); setDetail((p) => ({ ...p, [t]: d })); }
      catch (e) { setDetail((p) => ({ ...p, [t]: [] })); }
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-[#e6dcc4] bg-white">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-[#1b5e3f] text-left text-white">
              <th className="p-2.5 font-semibold">अ.क्र.</th>
              <th className="p-2.5 font-semibold">तहसील कार्यालयाचे नाव</th>
              <th className="p-2.5 text-center font-semibold">प्रारूप अर्ज</th>
              <th className="p-2.5 text-center font-semibold">एकूण प्राप्त अर्ज</th>
              <th className="p-2.5 text-center font-semibold">प्राथमिक पडताळणी झालेले (पात्र/अपात्र)</th>
              <th className="p-2.5 text-center font-semibold">प्राथमिक पडताळणीसाठी प्रलंबीत</th>
              <th className="p-2.5 text-center font-semibold">SDO कडील अर्ज (प्राप्त/निकाली)</th>
              <th className="p-2.5 text-center font-semibold">प्रत्यक्ष भेटीद्वारे पडताळणी झालेले</th>
              <th className="p-2.5 text-center font-semibold">प्रत्यक्ष भेट पडताळणीसाठी प्रलंबीत</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-slate-400">कोणतेही तहसील कार्यालय नाही</td></tr>}
            {rows.map((r, i) => (
              <React.Fragment key={r.tehsil}>
                <tr onClick={() => toggle(r.tehsil)} data-testid={`tehsil-office-${r.tehsil}`}
                  className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-[#faf7ef]">
                  <td className="p-2.5">{i + 1}</td>
                  <td className="p-2.5">
                    <span className="flex items-center gap-2 font-bold text-[#1b5e3f]">
                      {openT === r.tehsil ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      <Building2 className="h-4 w-4" /> {TEH_LABEL[r.tehsil] || r.tehsil} तहसील कार्यालय
                    </span>
                  </td>
                  <td className="p-2.5 text-center font-semibold text-slate-500">{r.draft || 0}</td>
                  <td className="p-2.5 text-center font-bold">{r.total}</td>
                  <td className="p-2.5 text-center"><span className="font-semibold text-emerald-700">{r.prelimEligible}</span><span className="text-slate-400"> / </span><span className="font-semibold text-red-600">{r.prelimIneligible}</span></td>
                  <td className="p-2.5 text-center font-semibold text-amber-700">{r.prelimPending}</td>
                  <td className="p-2.5 text-center" data-testid={`tehsil-sdo-${r.tehsil}`}><SdoCell r={r.sdoReceived} d={r.sdoDisposed} /></td>
                  <td className="p-2.5 text-center">{r.visitDone}</td>
                  <td className="p-2.5 text-center font-semibold text-amber-700">{r.visitPending}</td>
                </tr>
                {openT === r.tehsil && (
                  <tr>
                    <td colSpan={9} className="bg-[#faf7ef] p-4">
                      {detail[r.tehsil] === undefined
                        ? <p className="py-3 text-center text-slate-400">लोड होत आहे...</p>
                        : <StatsTable rows={detail[r.tehsil] || []} />}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {rows.length > 0 && <TotalRow rows={rows} totalDrafts={totalDrafts} />}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function TehsilOfficeDashboard() {
  return (
    <div>
      <div className="mb-4 rounded-2xl border border-[#e6dcc4] bg-white p-5">
        <p className="text-[15px] font-bold text-[#1b5e3f]">तहसील कार्यालयनिहाय अर्ज स्थिती</p>
        <p className="mt-1 text-[12.5px] text-slate-500">तहसील कार्यालयाच्या नावावर क्लिक करा — त्या तहसीलची स्थानिक स्वराज्य संस्थानिहाय माहिती उघडेल.</p>
      </div>
      <TehsilRows />
    </div>
  );
}
